/**
 * Read-only preview of a workout template — opened from the Today tab's
 * template tiles so the user can scan exercises before committing.
 *
 * Renders the same block-grouped layout the tracker uses, but with no
 * input controls. The footer carries the same `Start workout` action
 * that the tile's Start button does; users who already know the template
 * can still tap Start directly on the tile and bypass this screen.
 */

import { Icon } from '@k-os/ui';
import {
  useWorkoutTemplate,
  type ExerciseBlock,
  type TemplateExerciseDto,
  type WorkoutTemplateDto,
} from '../../../api/health';
import { CuesButton, EXERCISE_BLOCK_META, TypeChip } from './atoms';
import styles from './workouts.module.css';

const BLOCK_ORDER: ExerciseBlock[] = ['warmup', 'main', 'accessory', 'cooldown'];

export function WorkoutTemplatePreview({
  templateId,
  onStart,
  onBack,
  isStarting = false,
}: {
  templateId: string;
  onStart: (t: WorkoutTemplateDto) => void;
  onBack: () => void;
  isStarting?: boolean;
}) {
  const q = useWorkoutTemplate(templateId);

  if (q.isLoading) {
    return <div style={{ color: 'var(--ink-3)' }}>Loading template…</div>;
  }
  if (q.isError || !q.data) {
    return (
      <div>
        <div className={styles.crumb}>
          <span className={styles.crumbLink} onClick={onBack}>
            ← Today
          </span>
        </div>
        <div style={{ color: 'var(--neg)' }}>
          Couldn’t load this template.
        </div>
      </div>
    );
  }

  const template = q.data.template;
  const grouped = groupByBlock(template.exercises);
  const exerciseCount = template.exercises.length;

  return (
    <div>
      <div className={styles.crumb}>
        <span
          className={styles.crumbLink}
          onClick={onBack}
          role="button"
          tabIndex={0}
          style={{ cursor: 'pointer' }}
        >
          Today
        </span>
        <Icon name="chevron" size={11} />
        <span className={styles.crumbActive}>{template.name}</span>
      </div>

      <div className={styles.headerCard}>
        <div className={styles.headerTitleRow}>
          <h2 className={styles.headerName}>{template.name}</h2>
          <span className={styles.headerMeta}>
            {template.durationMin && <span>{template.durationMin} min</span>}
            {template.durationMin && <span aria-hidden>·</span>}
            <span>
              {exerciseCount} exercise{exerciseCount === 1 ? '' : 's'}
            </span>
            <span aria-hidden>·</span>
            <span>used {template.usedCount}×</span>
          </span>
        </div>
        {template.description && (
          <div className={styles.headerSub}>{template.description}</div>
        )}
      </div>

      {exerciseCount === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: 'var(--ink-3)',
            border: '1px dashed var(--line)',
            borderRadius: 8,
            background: 'var(--surface-2)',
          }}
        >
          This template has no exercises yet.
        </div>
      ) : (
        BLOCK_ORDER.filter((b) => grouped[b].length > 0).map((block) => (
          <div key={block} className={styles.block}>
            <div className={styles.blockHead}>
              <div>
                <div className={styles.blockHeadName}>
                  {EXERCISE_BLOCK_META[block].name}
                </div>
                <div className={styles.blockHeadDesc}>
                  {EXERCISE_BLOCK_META[block].desc}
                </div>
              </div>
              <span className={styles.blockHeadCount}>
                {grouped[block].length} exercise
                {grouped[block].length === 1 ? '' : 's'}
              </span>
            </div>
            {grouped[block].map((te) => (
              <PreviewExerciseRow key={te.id} te={te} />
            ))}
          </div>
        ))
      )}

      <div
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          marginTop: 22,
          paddingTop: 14,
          borderTop: '1px solid var(--line)',
        }}
      >
        <button type="button" className="kos-btn" onClick={onBack}>
          ← Back
        </button>
        <button
          type="button"
          className="kos-btn kos-btn-primary"
          style={{ marginLeft: 'auto' }}
          onClick={() => onStart(template)}
          disabled={isStarting || exerciseCount === 0}
        >
          <Icon name="check" size={12} />
          {isStarting ? 'Starting…' : 'Start workout'}
        </button>
      </div>
    </div>
  );
}

function PreviewExerciseRow({ te }: { te: TemplateExerciseDto }) {
  return (
    <div className={styles.ex}>
      <div className={styles.exHead}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={styles.exName}>{te.exercise.name}</div>
          <div className={styles.exMeta}>
            <TypeChip type={te.exercise.type} />
            {te.exercise.muscles.length > 0 && (
              <span style={{ marginLeft: 8 }}>
                {te.exercise.muscles.join(' · ')}
              </span>
            )}
          </div>
        </div>
        <div className={styles.exActions}>
          <CuesButton name={te.exercise.name} cues={te.exercise.cues} />
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 14,
          flexWrap: 'wrap',
          fontSize: 13,
          color: 'var(--ink-2)',
          fontFeatureSettings: "'tnum'",
        }}
      >
        {te.prescription && <span>{te.prescription}</span>}
        {te.defaultSets != null && (
          <span>
            {te.defaultSets} set{te.defaultSets === 1 ? '' : 's'}
          </span>
        )}
        {te.notes && (
          <span
            style={{
              fontFamily: 'inherit',
              fontStyle: 'italic',
              color: 'var(--ink-3)',
            }}
          >
            {te.notes}
          </span>
        )}
      </div>
    </div>
  );
}

function groupByBlock(
  items: TemplateExerciseDto[],
): Record<ExerciseBlock, TemplateExerciseDto[]> {
  const out: Record<ExerciseBlock, TemplateExerciseDto[]> = {
    warmup: [],
    main: [],
    accessory: [],
    cooldown: [],
  };
  // Preserve the position ordering the template author intended.
  const sorted = [...items].sort((a, b) => a.position - b.position);
  for (const te of sorted) out[te.block].push(te);
  return out;
}
